// 月曆頁的篩選狀態（W58.1 —— 從 `routes/Calendar.tsx` 搬出來）。
//
// **為什麼搬。** `routes/Calendar.tsx` 一直卡在 §3 #10 的 15 KB route
// 體積上限上，而 W58.0 / W58.1 又往它加了兩行週期的東西。這個檔案裡唯一
// 值得搬的是**篩選狀態與 URL 之間的雙向同步** —— 它有兩個讀取點、兩個
// 寫入點、一個 `useEffect`，而這些全是機械性的同步邏輯，不是版面。
//
// 同一個檔案裡早就有這條規矩的先例（`Calendar.tsx` 自己的註解）：
// 「純邏輯住在 `@/lib/heroSummary`，不在这個 route 裡，所以它不會把檔案
// 推過 15 KB」。
//
// **篩選活在 URL query params 裡**（DESIGN §8）：所以篩選撐得過重新載入、
// 撐得過分享連結、撐得過瀏覽器的上一頁。而月亮的 `y` / `m` 與篩選是
// **分開的參數** —— 混在一起會讓上一頁變得不可預測。

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { FilterState } from "@/components/calendar/FilterPanel";
import { FILTER_CATEGORY_ORDER } from "@/components/calendar/calendarIcons";
import type { CalendarCategory, OwnerFilter } from "@/lib/api/calendar";

/**
 * 七個類別 —— **一份清單**。
 *
 * `calendarIcons.FILTER_CATEGORY_ORDER` 是類別詞彙的**單一真相**（圖示、標籤、
 * 順序都在那裡，`calendarIcons.test.ts` 有一條測試釘住它是全部七個）。這裡
 * 以前自己寫了一份**一模一樣**的陣列 —— 而兩份一模一樣的清單遲早會漂移
 * （加了第四個類別而只改一處，篩選面板就會少一項）。
 */
const ALL_CATEGORIES: CalendarCategory[] = [...FILTER_CATEGORY_ORDER];

/** 每日顯示上限的預設值（後端的 `DEFAULT_PER_DAY` 也是 3）。 */
export const DEFAULT_MAX_PER_DAY = 3;

export interface CalendarFilters {
  filter: FilterState;
  setFilter: (next: FilterState) => void;
}

export function useCalendarFilters(): CalendarFilters {
  const [params, setParams] = useSearchParams();

  const filter = useMemo<FilterState>(
    () => ({
      categories:
        (params.get("categories")?.split(",").filter(Boolean) as CalendarCategory[]) ??
        ALL_CATEGORIES,
      owner: (params.get("owner") as OwnerFilter) ?? "all",
      maxPerDay: Number(params.get("cap")) || DEFAULT_MAX_PER_DAY,
    }),
    [params],
  );

  const setFilter = useCallback(
    (next: FilterState) => {
      const p = new URLSearchParams(params);
      // **預設值不寫進 URL** —— 全部篩選與預設上限時，`categories` / `owner` /
      // `cap` 這三個參數會被刪掉，所以分享出去的連結是最短的那一個。
      if (next.categories.length === ALL_CATEGORIES.length) p.delete("categories");
      else p.set("categories", next.categories.join(","));
      if (next.owner === "all") p.delete("owner");
      else p.set("owner", next.owner);
      if (next.maxPerDay === DEFAULT_MAX_PER_DAY) p.delete("cap");
      else p.set("cap", String(next.maxPerDay));
      setParams(p, { replace: true });
    },
    [params, setParams],
  );

  return { filter, setFilter };
}
